import type {
  ExpenseCategory,
  Prisma,
  PrismaClient,
  PurchaseOrder,
  PurchaseOrderLine,
  VendorBill,
  VendorBillLine,
} from '@prisma/client';
import Decimal from 'decimal.js';
import { z } from 'zod';
import { HttpError } from '../../core/errors.js';
import { nextDocumentNumber } from '../../core/sequence.js';
import { postEntry, reverseEntry, type PostingLine } from '../accounting/posting.service.js';
import type { AuthContext } from '../auth/auth.types.js';
import { BASE_CURRENCY, computeTotals, type CalcLineInput } from '../sales/calc.js';
import { addDays, isoOf, logEvent, toDate } from '../sales/documents.service.js';

export type Tx = Prisma.TransactionClient;
export type Db = PrismaClient | Tx;
export const dec = (v: Decimal.Value | { toString(): string }) => new Decimal(v.toString());
export const baseRound = (v: Decimal) => v.toDecimalPlaces(0, Decimal.ROUND_HALF_UP);

const num = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .refine((v) => /^-?\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid number');
const positive = num.refine((v) => Number(v) > 0, 'Must be greater than zero');
const nonNegative = num.refine((v) => Number(v) >= 0, 'Cannot be negative');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

// ───────────────────────── expense categories ─────────────────────────

/** First use: one category per ledger expense account, so Bills and Expenses have sensible choices. */
export async function ensureExpenseCategories(db: Db, companyId: number) {
  if ((await db.expenseCategory.count({ where: { companyId } })) > 0) return;
  const accounts = await db.account.findMany({
    where: { companyId, type: 'expense', isActive: true },
    orderBy: { code: 'asc' },
  });
  for (const a of accounts) {
    await db.expenseCategory
      .create({ data: { companyId, name: a.name, accountId: a.id } })
      .catch(() => undefined);
  }
}

export const categoryView = (c: ExpenseCategory, accountCode?: string) => ({
  id: c.id,
  name: c.name,
  accountId: c.accountId,
  accountCode,
  vatCode: c.vatCode,
  active: c.active,
  needsReview: c.needsReview,
});

/**
 * Create-in-place category (PLAN.md 7.9.3): makes a new ledger expense account for it, so reports keep
 * working, and flags it for the accountant to review.
 */
export async function createCategory(
  tx: Tx,
  args: {
    companyId: number;
    userId: number;
    name: string;
    vatCode?: string;
    needsReview?: boolean;
  },
) {
  const name = args.name.trim();
  const dup = await tx.expenseCategory.findFirst({
    where: { companyId: args.companyId, name },
  });
  if (dup) throw new HttpError(409, 'DUPLICATE', 'A category with this name already exists');
  // Serialise code allocation: two categories created at once must not pick the same account code.
  await tx.$queryRaw`SELECT id FROM companies WHERE id = ${args.companyId} FOR UPDATE`;
  const used = await tx.account.findMany({
    where: { companyId: args.companyId, code: { gte: '6000', lte: '6999' } },
    select: { code: true },
  });
  const taken = new Set(used.map((u) => Number(u.code)));
  let code = 6010;
  while (taken.has(code) && code < 6999) code += 1;
  if (code >= 6999) throw new HttpError(409, 'NO_CODES', 'No free expense account codes left');
  const account = await tx.account.create({
    data: {
      companyId: args.companyId,
      code: String(code),
      name,
      type: 'expense',
      subtype: 'opex',
    },
  });
  return tx.expenseCategory.create({
    data: {
      companyId: args.companyId,
      name,
      accountId: account.id,
      vatCode: args.vatCode ?? 'VAT_STD',
      needsReview: args.needsReview ?? true,
      createdBy: args.userId,
    },
  });
}

async function defaultCategoryId(db: Db, companyId: number): Promise<number> {
  await ensureExpenseCategories(db, companyId);
  const other = await db.expenseCategory.findFirst({
    where: { companyId, name: 'Other Expenses' },
  });
  const any = other ?? (await db.expenseCategory.findFirst({ where: { companyId, active: true } }));
  if (!any) throw new HttpError(422, 'NO_CATEGORY', 'Add an expense category first');
  return any.id;
}

// ───────────────────────── shared input + pricing ─────────────────────────

export const purchaseLineSchema = z.object({
  itemId: z.number().int().positive().nullable().optional(),
  categoryId: z.number().int().positive().nullable().optional(),
  description: z.string().trim().min(1, 'Description is required').max(1000),
  quantity: positive.default('1'),
  unit: z.string().trim().max(20).nullable().optional(),
  unitPrice: nonNegative.default('0'),
  discountType: z.enum(['none', 'percent', 'amount']).default('none'),
  discountValue: nonNegative.default('0'),
  vatCode: z.string().trim().min(2).max(30).default('VAT_STD'),
});
export type PurchaseLineInput = z.infer<typeof purchaseLineSchema>;

const currencyField = z
  .string()
  .trim()
  .length(3)
  .transform((v) => v.toUpperCase())
  .optional();

export const orderInputSchema = z.object({
  partyId: z.number().int().positive(),
  currency: currencyField,
  exchangeRate: positive.optional(),
  issueDate: isoDate,
  expectedDate: isoDate.nullable().optional(),
  reference: z.string().trim().max(100).nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
  terms: z.string().trim().max(4000).nullable().optional(),
  lines: z.array(purchaseLineSchema).min(1, 'Add at least one line').max(200),
});
export type OrderInput = z.infer<typeof orderInputSchema>;

export const billInputSchema = z.object({
  partyId: z.number().int().positive(),
  vendorRef: z.string().trim().max(100).nullable().optional(),
  orderId: z.number().int().positive().nullable().optional(),
  currency: currencyField,
  exchangeRate: positive.optional(),
  issueDate: isoDate,
  dueDate: isoDate.nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
  whtApplicable: z.boolean().optional(),
  lines: z.array(purchaseLineSchema).min(1, 'Add at least one line').max(200),
  /** Set by "convert from purchase order": which order line each bill line consumes. */
  orderLineIds: z.array(z.number().int().positive().nullable()).optional(),
});
export type BillInput = z.infer<typeof billInputSchema>;

async function resolveRate(
  db: Db,
  companyId: number,
  currency: string,
  date: Date,
  given?: string,
): Promise<Decimal> {
  if (currency === BASE_CURRENCY) return new Decimal(1);
  if (given) return dec(given);
  const latest = await db.exchangeRate.findFirst({
    where: { companyId, currency, effectiveDate: { lte: date } },
    orderBy: { effectiveDate: 'desc' },
  });
  if (!latest)
    throw new HttpError(
      422,
      'NO_EXCHANGE_RATE',
      `Enter an exchange rate for ${currency} (Settings > Tax & Currencies) or type one on the document`,
    );
  return dec(latest.rate);
}

async function priceLines(
  tx: Db,
  args: {
    companyId: number;
    currency: string;
    exchangeRate: Decimal;
    lines: PurchaseLineInput[];
    requireCategory: boolean;
  },
) {
  const rates = await tx.taxRate.findMany({
    where: { companyId: args.companyId, kind: 'vat', active: true },
  });
  const fallback = args.requireCategory ? await defaultCategoryId(tx, args.companyId) : null;
  const cats = await tx.expenseCategory.findMany({ where: { companyId: args.companyId } });
  const resolved = args.lines.map((l, i) => {
    const rate = rates.find((r) => r.code === l.vatCode);
    if (!rate)
      throw new HttpError(
        400,
        'VALIDATION_ERROR',
        `Line ${i + 1}: VAT category "${l.vatCode}" does not exist`,
      );
    const categoryId = l.categoryId ?? fallback;
    if (categoryId && !cats.some((c) => c.id === categoryId && c.active))
      throw new HttpError(400, 'VALIDATION_ERROR', `Line ${i + 1}: expense category not found`);
    return { ...l, categoryId: categoryId ?? null, vatRate: dec(rate.rate) };
  });
  const calc = computeTotals(
    resolved.map<CalcLineInput>((l) => ({
      kind: 'item',
      quantity: l.quantity,
      unitPrice: l.unitPrice,
      discountType: l.discountType,
      discountValue: l.discountValue,
      vatRate: l.vatRate,
    })),
    { currency: args.currency, exchangeRate: args.exchangeRate },
  );
  const lineData = resolved.map((l, i) => {
    const c = calc.lines[i]!;
    return {
      lineNo: i + 1,
      itemId: l.itemId ?? null,
      categoryId: l.categoryId,
      description: l.description,
      quantity: l.quantity,
      unit: l.unit ?? null,
      unitPrice: l.unitPrice,
      discountType: l.discountType,
      discountValue: l.discountValue,
      vatCode: l.vatCode,
      vatRate: l.vatRate.toString(),
      lineDiscount: c.lineDiscount.toString(),
      lineNet: c.lineNet.toString(),
      lineTax: c.lineTax.toString(),
      lineTotal: c.lineTotal.toString(),
    };
  });
  return { calc, lineData };
}

async function loadVendor(tx: Db, companyId: number, partyId: number) {
  const party = await tx.party.findFirst({ where: { id: partyId, companyId, active: true } });
  if (!party || !party.isVendor)
    throw new HttpError(
      400,
      'VALIDATION_ERROR',
      'Choose a vendor (mark the party as a vendor first)',
    );
  return party;
}

const ownScope = (auth: AuthContext) => (auth.scope === 'own' ? { ownerId: auth.userId } : {});

// ───────────────────────── purchase orders ─────────────────────────

export type OrderWithLines = PurchaseOrder & { lines: PurchaseOrderLine[] };
export const orderInclude = { lines: { orderBy: { lineNo: 'asc' as const } } };

export async function loadOrder(db: Db, auth: AuthContext, id: number): Promise<OrderWithLines> {
  const o = await db.purchaseOrder.findFirst({
    where: { id, companyId: auth.companyId, ...ownScope(auth) },
    include: orderInclude,
  });
  if (!o) throw new HttpError(404, 'NOT_FOUND', 'Purchase order not found');
  return o;
}

export async function saveOrderDraft(
  tx: Tx,
  args: { auth: AuthContext; id?: number; input: OrderInput },
): Promise<OrderWithLines> {
  const { auth, input } = args;
  const party = await loadVendor(tx, auth.companyId, input.partyId);
  const issueDate = toDate(input.issueDate);
  const currency = input.currency ?? party.currency;
  const rate = await resolveRate(tx, auth.companyId, currency, issueDate, input.exchangeRate);
  const { calc, lineData } = await priceLines(tx, {
    companyId: auth.companyId,
    currency,
    exchangeRate: rate,
    lines: input.lines,
    requireCategory: false,
  });
  const expected = input.expectedDate ? toDate(input.expectedDate) : addDays(issueDate, 14);
  if (expected < issueDate)
    throw new HttpError(
      400,
      'VALIDATION_ERROR',
      'Expected delivery cannot be before the order date',
    );
  const base = {
    partyId: party.id,
    currency,
    exchangeRate: rate.toString(),
    issueDate,
    expectedDate: expected,
    reference: input.reference ?? null,
    notes: input.notes ?? null,
    terms: input.terms ?? null,
    subtotal: calc.subtotal.toString(),
    discountTotal: calc.discountTotal.toString(),
    taxTotal: calc.taxTotal.toString(),
    total: calc.total.toString(),
    baseTotal: calc.baseTotal.toString(),
    updatedBy: auth.userId,
  };
  if (args.id) {
    const existing = await tx.purchaseOrder.findFirst({
      where: { id: args.id, companyId: auth.companyId, ...ownScope(auth) },
    });
    if (!existing) throw new HttpError(404, 'NOT_FOUND', 'Purchase order not found');
    if (!['draft', 'pending_approval'].includes(existing.status))
      throw new HttpError(409, 'LOCKED', 'Only draft purchase orders can be edited');
    await tx.purchaseOrderLine.deleteMany({ where: { orderId: existing.id } });
    return tx.purchaseOrder.update({
      where: { id: existing.id },
      data: { ...base, status: 'draft', lines: { create: lineData } },
      include: orderInclude,
    });
  }
  return tx.purchaseOrder.create({
    data: {
      companyId: auth.companyId,
      ownerId: auth.userId,
      createdBy: auth.userId,
      status: 'draft',
      ...base,
      lines: { create: lineData },
    },
    include: orderInclude,
  });
}

export function assertApprovalLimit(
  auth: AuthContext,
  baseTotal: Decimal.Value | { toString(): string },
) {
  if (auth.approvalLimit !== null && dec(baseTotal).gt(auth.approvalLimit)) {
    throw new HttpError(
      403,
      'APPROVAL_LIMIT_EXCEEDED',
      `This is above your approval limit (UGX ${Number(auth.approvalLimit).toLocaleString('en-US')}). Submit it for approval by someone with a higher limit.`,
    );
  }
}

export async function approveOrder(tx: Tx, auth: AuthContext, id: number) {
  const o = await loadOrder(tx, auth, id);
  if (!['draft', 'pending_approval'].includes(o.status))
    throw new HttpError(409, 'ALREADY_APPROVED', 'This purchase order has already been approved');
  const number =
    o.number ?? (await nextDocumentNumber(tx, auth.companyId, 'purchaseorder', o.issueDate));
  const updated = await tx.purchaseOrder.update({
    where: { id },
    data: {
      number,
      status: 'approved',
      approvedAt: new Date(),
      approvedBy: auth.userId,
      updatedBy: auth.userId,
    },
    include: orderInclude,
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'purchase_order',
    entityId: id,
    type: 'approved',
    userId: auth.userId,
    note: number,
  });
  return updated;
}

export async function receiveOrder(
  tx: Tx,
  auth: AuthContext,
  id: number,
  received: { lineId: number; quantity: string }[],
) {
  const o = await loadOrder(tx, auth, id);
  if (!['approved', 'sent', 'partially_received'].includes(o.status))
    throw new HttpError(409, 'INVALID_STATE', 'Only approved orders can receive goods');
  for (const r of received) {
    const line = o.lines.find((l) => l.id === r.lineId);
    if (!line) throw new HttpError(404, 'NOT_FOUND', 'Order line not found');
    const qty = dec(r.quantity);
    if (qty.lte(0)) continue;
    const next = dec(line.receivedQty).plus(qty);
    if (next.gt(dec(line.quantity)))
      throw new HttpError(
        409,
        'OVER_RECEIVED',
        `"${line.description}": only ${dec(line.quantity).minus(dec(line.receivedQty)).toString()} still to receive`,
      );
    await tx.purchaseOrderLine.update({
      where: { id: line.id },
      data: { receivedQty: next.toString() },
    });
  }
  const fresh = await tx.purchaseOrderLine.findMany({ where: { orderId: id } });
  const all = fresh.every((l) => dec(l.receivedQty).gte(dec(l.quantity)));
  const some = fresh.some((l) => dec(l.receivedQty).gt(0));
  const status = all ? 'received' : some ? 'partially_received' : o.status;
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'purchase_order',
    entityId: id,
    type: 'received',
    userId: auth.userId,
    note: status,
  });
  return tx.purchaseOrder.update({ where: { id }, data: { status }, include: orderInclude });
}

/** Creates a draft bill from (part of) an order: remaining unbilled quantities unless told otherwise. */
export async function orderToBill(
  tx: Tx,
  auth: AuthContext,
  id: number,
  picks?: { lineId: number; quantity: string }[],
) {
  const o = await loadOrder(tx, auth, id);
  if (!['approved', 'sent', 'partially_received', 'received'].includes(o.status))
    throw new HttpError(409, 'INVALID_STATE', 'Approve the purchase order before billing it');
  const lines: PurchaseLineInput[] = [];
  const orderLineIds: number[] = [];
  for (const l of o.lines) {
    const remaining = dec(l.quantity).minus(dec(l.billedQty));
    const pick = picks?.find((p) => p.lineId === l.id);
    const qty = picks ? (pick ? dec(pick.quantity) : new Decimal(0)) : remaining;
    if (qty.lte(0)) continue;
    if (qty.gt(remaining))
      throw new HttpError(
        409,
        'OVER_BILLED',
        `"${l.description}": only ${remaining.toString()} left to bill`,
      );
    const ratio = qty.div(dec(l.quantity));
    lines.push({
      itemId: l.itemId,
      categoryId: l.categoryId,
      description: l.description,
      quantity: qty.toString(),
      unit: l.unit,
      unitPrice: l.unitPrice.toString(),
      discountType: l.discountType as 'none' | 'percent' | 'amount',
      // A fixed-amount discount is shared out in proportion to the quantity billed.
      discountValue:
        l.discountType === 'amount'
          ? dec(l.discountValue).times(ratio).toDecimalPlaces(4).toString()
          : l.discountValue.toString(),
      vatCode: l.vatCode,
    });
    orderLineIds.push(l.id);
  }
  if (lines.length === 0)
    throw new HttpError(409, 'NOTHING_TO_BILL', 'Everything on this order has already been billed');
  const party = await tx.party.findUniqueOrThrow({ where: { id: o.partyId } });
  return saveBillDraft(tx, {
    auth,
    input: {
      partyId: o.partyId,
      orderId: o.id,
      currency: o.currency,
      exchangeRate: o.exchangeRate.toString(),
      issueDate: new Date().toISOString().slice(0, 10),
      dueDate: isoOf(addDays(new Date(), party.paymentTermsDays)),
      whtApplicable: party.whtApplicable,
      notes: o.reference ? `PO ${o.number} (${o.reference})` : `PO ${o.number}`,
      lines,
      orderLineIds,
    },
  });
}

// ───────────────────────── vendor bills ─────────────────────────

export type BillWithLines = VendorBill & { lines: VendorBillLine[] };
export const billInclude = { lines: { orderBy: { lineNo: 'asc' as const } } };

export async function loadBill(db: Db, auth: AuthContext, id: number): Promise<BillWithLines> {
  const b = await db.vendorBill.findFirst({
    where: { id, companyId: auth.companyId, ...ownScope(auth) },
    include: billInclude,
  });
  if (!b) throw new HttpError(404, 'NOT_FOUND', 'Bill not found');
  return b;
}

export async function saveBillDraft(
  tx: Tx,
  args: { auth: AuthContext; id?: number; input: BillInput },
): Promise<BillWithLines> {
  const { auth, input } = args;
  const party = await loadVendor(tx, auth.companyId, input.partyId);
  const issueDate = toDate(input.issueDate);
  const currency = input.currency ?? party.currency;
  const rate = await resolveRate(tx, auth.companyId, currency, issueDate, input.exchangeRate);
  const { calc, lineData } = await priceLines(tx, {
    companyId: auth.companyId,
    currency,
    exchangeRate: rate,
    lines: input.lines,
    requireCategory: true,
  });
  const dueDate = input.dueDate
    ? toDate(input.dueDate)
    : addDays(issueDate, party.paymentTermsDays);
  if (dueDate < issueDate)
    throw new HttpError(400, 'VALIDATION_ERROR', 'Due date cannot be before the bill date');
  if (input.orderId) {
    const o = await tx.purchaseOrder.findFirst({
      where: { id: input.orderId, companyId: auth.companyId, partyId: party.id },
    });
    if (!o)
      throw new HttpError(400, 'VALIDATION_ERROR', 'Purchase order not found for this vendor');
  }
  if (input.vendorRef) {
    const dup = await tx.vendorBill.findFirst({
      where: {
        companyId: auth.companyId,
        partyId: party.id,
        vendorRef: input.vendorRef,
        status: { not: 'void' },
        ...(args.id ? { id: { not: args.id } } : {}),
      },
    });
    if (dup)
      throw new HttpError(
        409,
        'DUPLICATE_BILL',
        `This vendor's bill ${input.vendorRef} is already recorded${dup.number ? ` as ${dup.number}` : ''}`,
      );
  }
  const withOrder = lineData.map((l, i) => ({
    ...l,
    categoryId: l.categoryId!,
    orderLineId: input.orderLineIds?.[i] ?? null,
  }));
  const base = {
    partyId: party.id,
    vendorRef: input.vendorRef ?? null,
    orderId: input.orderId ?? null,
    currency,
    exchangeRate: rate.toString(),
    issueDate,
    dueDate,
    notes: input.notes ?? null,
    whtApplicable: input.whtApplicable ?? party.whtApplicable,
    subtotal: calc.subtotal.toString(),
    discountTotal: calc.discountTotal.toString(),
    taxTotal: calc.taxTotal.toString(),
    total: calc.total.toString(),
    baseTotal: calc.baseTotal.toString(),
    updatedBy: auth.userId,
  };
  if (args.id) {
    const existing = await tx.vendorBill.findFirst({
      where: { id: args.id, companyId: auth.companyId, ...ownScope(auth) },
    });
    if (!existing) throw new HttpError(404, 'NOT_FOUND', 'Bill not found');
    if (existing.status !== 'draft')
      throw new HttpError(
        409,
        'LOCKED',
        'Approved bills cannot be edited. Void it and enter it again.',
      );
    await tx.vendorBillLine.deleteMany({ where: { billId: existing.id } });
    return tx.vendorBill.update({
      where: { id: existing.id },
      data: { ...base, lines: { create: withOrder } },
      include: billInclude,
    });
  }
  const created = await tx.vendorBill.create({
    data: {
      companyId: auth.companyId,
      ownerId: auth.userId,
      createdBy: auth.userId,
      status: 'draft',
      ...base,
      lines: { create: withOrder },
    },
    include: billInclude,
  });
  return created;
}

/** Base-currency debit lines for a bill: net per category account, then VAT input. Credit AP for the sum. */
function billPostingLines(
  bill: BillWithLines,
  cats: Map<number, number>,
  label: string,
): PostingLine[] {
  const rate = dec(bill.exchangeRate);
  const byAccount = new Map<number, Decimal>();
  let vat = new Decimal(0);
  for (const l of bill.lines) {
    const accountId = cats.get(l.categoryId);
    if (!accountId) throw new HttpError(422, 'NO_CATEGORY', 'A bill line has no expense category');
    const net = baseRound(dec(l.lineNet).times(rate));
    byAccount.set(accountId, (byAccount.get(accountId) ?? new Decimal(0)).plus(net));
    vat = vat.plus(baseRound(dec(l.lineTax).times(rate)));
  }
  const lines: PostingLine[] = [];
  let total = new Decimal(0);
  for (const [accountId, amount] of byAccount) {
    if (amount.isZero()) continue;
    total = total.plus(amount);
    lines.push({ accountId, debit: amount.toFixed(4), memo: label });
  }
  if (!vat.isZero()) {
    total = total.plus(vat);
    lines.push({ systemKey: 'VAT_IN', debit: vat.toFixed(4), memo: label });
  }
  lines.push({ systemKey: 'AP', credit: total.toFixed(4), memo: label });
  return lines;
}

export async function approveBill(tx: Tx, auth: AuthContext, id: number) {
  const bill = await loadBill(tx, auth, id);
  if (bill.status !== 'draft')
    throw new HttpError(409, 'ALREADY_APPROVED', 'This bill has already been approved');
  const number =
    bill.number ?? (await nextDocumentNumber(tx, auth.companyId, 'bill', bill.issueDate));
  const party = await tx.party.findUniqueOrThrow({ where: { id: bill.partyId } });
  const cats = new Map(
    (await tx.expenseCategory.findMany({ where: { companyId: auth.companyId } })).map((c) => [
      c.id,
      c.accountId,
    ]),
  );
  const entry = await postEntry(tx, {
    companyId: auth.companyId,
    date: bill.issueDate,
    description: `Bill ${number}: ${party.name}`,
    sourceType: 'bill',
    sourceId: bill.id,
    reference: bill.vendorRef ?? number,
    lines: billPostingLines(bill, cats, number),
    userId: auth.userId,
  });
  // Billing an order consumes its lines.
  for (const l of bill.lines) {
    if (!l.orderLineId) continue;
    const ol = await tx.purchaseOrderLine.findUnique({ where: { id: l.orderLineId } });
    if (ol)
      await tx.purchaseOrderLine.update({
        where: { id: ol.id },
        data: { billedQty: dec(ol.billedQty).plus(dec(l.quantity)).toString() },
      });
  }
  const updated = await tx.vendorBill.update({
    where: { id },
    data: {
      number,
      status: 'approved',
      approvedAt: new Date(),
      approvedBy: auth.userId,
      journalEntryId: entry.id,
      updatedBy: auth.userId,
    },
    include: billInclude,
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'vendor_bill',
    entityId: id,
    type: 'approved',
    userId: auth.userId,
    note: number,
  });
  return updated;
}

export const outstandingOf = (d: { total: Prisma.Decimal; amountPaid: Prisma.Decimal }) =>
  Decimal.max(dec(d.total).minus(dec(d.amountPaid)), 0);

export async function refreshBillStatus(tx: Tx, billId: number) {
  const b = await tx.vendorBill.findUniqueOrThrow({ where: { id: billId } });
  if (b.status === 'void' || b.status === 'draft') return;
  const paid = dec(b.amountPaid);
  const status = paid.lte(0) ? 'approved' : paid.gte(dec(b.total)) ? 'paid' : 'partially_paid';
  if (status !== b.status) await tx.vendorBill.update({ where: { id: billId }, data: { status } });
}

export async function voidBill(tx: Tx, auth: AuthContext, id: number, reason: string) {
  const bill = await loadBill(tx, auth, id);
  if (bill.status === 'void') throw new HttpError(409, 'ALREADY_VOID', 'This bill is already void');
  if (bill.status !== 'draft') {
    const paid = await tx.payoutAllocation.count({
      where: { targetType: 'bill', targetId: id, payout: { status: 'posted' } },
    });
    if (paid > 0 || dec(bill.amountPaid).gt(0))
      throw new HttpError(409, 'HAS_PAYMENTS', 'This bill has payouts. Void those first.');
    if (bill.journalEntryId)
      await reverseEntry(tx, {
        companyId: auth.companyId,
        entryId: bill.journalEntryId,
        reason: `Void ${bill.number}: ${reason}`,
        date: new Date().toISOString().slice(0, 10),
        userId: auth.userId,
      });
    // Give the quantities back to the purchase order.
    for (const l of bill.lines) {
      if (!l.orderLineId) continue;
      const ol = await tx.purchaseOrderLine.findUnique({ where: { id: l.orderLineId } });
      if (ol)
        await tx.purchaseOrderLine.update({
          where: { id: ol.id },
          data: { billedQty: Decimal.max(0, dec(ol.billedQty).minus(dec(l.quantity))).toString() },
        });
    }
  }
  const updated = await tx.vendorBill.update({
    where: { id },
    data: { status: 'void', voidedAt: new Date(), voidReason: reason, updatedBy: auth.userId },
    include: billInclude,
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'vendor_bill',
    entityId: id,
    type: 'void',
    userId: auth.userId,
    note: reason,
  });
  return updated;
}
